import { useState } from 'react';
import { User, Bot, Copy, Check, RefreshCw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../utils/api';
import { Message } from '../stores/chatStore';
import ArtifactRenderer from './ArtifactRenderer';

interface MessageItemProps {
  message: Message;
  isStreaming?: boolean;
  isLast?: boolean;
}

export default function MessageItem({ message, isStreaming, isLast }: MessageItemProps) {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Parse artifacts from message content
  const parseArtifacts = (content: string) => {
    const artifactRegex = /<artifact\s+type="([^"]+)"\s+title="([^"]+)"(?:\s+language="([^"]+)")?>([\s\S]*?)<\/artifact>/g;
    const artifacts: Array<{ type: string; title: string; language?: string; content: string }> = [];
    let match;
    while ((match = artifactRegex.exec(content)) !== null) {
      artifacts.push({
        type: match[1],
        title: match[2],
        language: match[3],
        content: match[4].trim(),
      });
    }
    return artifacts;
  };

  // Clean content by removing artifact tags
  const cleanContent = (content: string) => {
    return content.replace(/<artifact[^>]*>[\s\S]*?<\/artifact>/g, '');
  };

  const artifacts = parseArtifacts(message.content);
  const displayContent = cleanContent(message.content);

  return (
    <div
      className={cn(
        "flex gap-4 message-enter",
        isUser ? "flex-row-reverse" : "flex-row"
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
          isUser ? "bg-indigo-600" : "bg-gradient-to-br from-purple-600 to-indigo-600"
        )}
      >
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>

      {/* Content */}
      <div className={cn("flex-1 min-w-0", isUser && "text-right")}>
        <div
          className={cn(
            "inline-block max-w-full text-left",
            isUser
              ? "bg-indigo-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5"
              : "text-gray-100"
          )}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{displayContent}</p>
          ) : (
            <div className="prose prose-invert prose-sm max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayContent}
              </ReactMarkdown>
              {isStreaming && isLast && <span className="stream-cursor" />}
            </div>
          )}
        </div>

        {/* Artifacts */}
        {!isUser && artifacts.length > 0 && (
          <div className="mt-4 space-y-4">
            {artifacts.map((artifact, index) => (
              <ArtifactRenderer
                key={index}
                type={artifact.type as any}
                title={artifact.title}
                content={artifact.content}
                language={artifact.language}
              />
            ))}
          </div>
        )}

        {/* Actions */}
        {!isUser && !isStreaming && (
          <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={handleCopy}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
              title="Copy message"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
            <button
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
              title="Regenerate"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
